"use client";

import {
  MapCanvasProvider,
  type MapCanvasProviderProps
} from "@gettactic/map-builder/src/components/map/contexts/MapCanvasContext/MapCanvasContext";
import { useMapCanvas } from "@gettactic/map-builder/src/components/map/contexts/MapCanvasContext/useMapCanvas";
import { useMapCanvasShapes } from "@gettactic/map-builder/src/components/map/contexts/MapCanvasContext/useMapCanvasShapes";
import { useMapCanvasStage } from "@gettactic/map-builder/src/components/map/contexts/MapCanvasContext/useMapCanvasStage";
import { useMapCanvasStagePosition } from "@gettactic/map-builder/src/components/map/contexts/MapCanvasContext/useMapCanvasStagePosition";
import { ZoneLegend } from "@gettactic/map-builder/src/components/ZoneLegend/ZoneLegend";
import { ZoomControls } from "@gettactic/map-builder/src/components/ZoomControls";
import React from "react";
import { DocumentLayer } from "../render/DocumentLayer";
import { viewportTransform } from "../render/MapSvg";
import {
  type RenderOptions,
  RenderOptionsProvider
} from "../render/RenderOptionsContext";
import { createEditorStore } from "../store/createEditorStore";
import { EditorStoreProvider } from "../store/EditorStoreContext";
import { computeLegendData } from "./legendData";
import { useViewerPanZoom } from "./useViewerPanZoom";

export interface MapSvgViewProps
  extends Pick<
    MapCanvasProviderProps,
    | "value"
    | "initialPosition"
    | "interactiveZones"
    | "highlightResource"
    | "onZoneLinkClick"
    | "isDark"
    | "isPrivacyMode"
    | "authenticatedUser"
    | "resourceColors"
    | "zoneColors"
    | "teamColors"
  > {
  style?: React.SVGProps<SVGSVGElement>["style"];
  /** Crop to one region of the map, as `ViewSVG` does for previews. */
  focusBounds?: { x: number; y: number; width: number; height: number };
  /** Own pan, zoom and zoom controls. Off by default: the map page pans
   *  and zooms the `ViewSVG`-shaped element itself. */
  interactive?: boolean;
  /** Suppress the zone legend (a host drawing its own, or the editor). */
  hideZoneLegend?: boolean;
  /** Forwarded to the legend, see its `bottomOffset`. */
  legendBottomOffset?: number | string;
  children?: React.ReactNode;
}

/**
 * The map viewer drawn by the editor's renderer: a drop-in for the
 * Konva/`ViewSVG` `MapView` with the same provider, outer markup and
 * pan/zoom, so the two can be compared on the real map page. It draws
 * the document only: the viewer's legend, zone tooltips, linked icons and
 * per-resource click targets are not wired yet.
 */
export const MapSvgView = React.forwardRef<
  SVGSVGElement | null,
  MapSvgViewProps
>(
  (
    {
      style,
      focusBounds,
      interactive = false,
      hideZoneLegend = false,
      legendBottomOffset,
      children,
      ...providerProps
    },
    ref
  ) => (
    <MapCanvasProvider {...providerProps} isViewMode>
      <ViewerSvg
        ref={ref}
        style={style}
        focusBounds={focusBounds}
        interactive={interactive}
        hideZoneLegend={hideZoneLegend}
        legendBottomOffset={legendBottomOffset}
      />
      {children}
    </MapCanvasProvider>
  )
);
MapSvgView.displayName = "MapSvgView";

interface ViewerSvgProps {
  style?: React.SVGProps<SVGSVGElement>["style"];
  focusBounds?: MapSvgViewProps["focusBounds"];
  interactive: boolean;
  hideZoneLegend: boolean;
  legendBottomOffset?: number | string;
}

const ViewerSvg = React.forwardRef<SVGSVGElement | null, ViewerSvgProps>(
  (
    { style, focusBounds, interactive, hideZoneLegend, legendBottomOffset },
    ref
  ) => {
    const {
      editingZoneWidth,
      editingZoneHeight,
      zoneTypes,
      zoneColors,
      interactiveZones,
      teamColors,
      gridSize,
      spotLabelPattern,
      lastSpotLabel,
      highlightResource,
      onZoneLinkClick,
      pixelsPerMeter,
      authenticatedUser
    } = useMapCanvas();
    const { shapes } = useMapCanvasShapes();
    const { stagePosition } = useMapCanvasStagePosition();
    const { stagePositionRef, updateStagePosition } = useMapCanvasStage();
    const containerRef = React.useRef<HTMLDivElement | null>(null);
    const rootRef = React.useRef<SVGGElement | null>(null);
    const commit = React.useCallback(
      (position: { x: number; y: number; scale: number }) =>
        updateStagePosition(position),
      [updateStagePosition]
    );
    useViewerPanZoom({
      containerRef,
      rootRef,
      positionRef: stagePositionRef,
      commit
    });

    // Fit the floor to the container once, unless the host set a position.
    const fitToContainer = React.useCallback(() => {
      const bounds = containerRef.current?.getBoundingClientRect();
      if (!bounds || bounds.width === 0 || editingZoneWidth === 0) return;
      const scale =
        Math.min(
          bounds.width / editingZoneWidth,
          bounds.height / editingZoneHeight
        ) * 0.95;
      updateStagePosition({
        x: bounds.width / 2 - (editingZoneWidth / 2) * scale,
        y: bounds.height / 2 - (editingZoneHeight / 2) * scale,
        scale
      });
    }, [editingZoneWidth, editingZoneHeight, updateStagePosition]);
    const hasFitted = React.useRef(false);
    React.useEffect(() => {
      if (hasFitted.current || focusBounds || !interactive) return;
      const { x, y, scale } = stagePositionRef.current;
      if (x !== 0 || y !== 0 || scale !== 1) return;
      hasFitted.current = true;
      fitToContainer();
    }, [fitToContainer, focusBounds, stagePositionRef]);

    const zoomBy = (factor: number) => {
      const bounds = containerRef.current?.getBoundingClientRect();
      const current = stagePositionRef.current;
      const scale = Math.min(Math.max(current.scale * factor, 0.1), 5);
      const centre = bounds
        ? { x: bounds.width / 2, y: bounds.height / 2 }
        : { x: 0, y: 0 };
      updateStagePosition({
        x: centre.x - ((centre.x - current.x) / current.scale) * scale,
        y: centre.y - ((centre.y - current.y) / current.scale) * scale,
        scale
      });
    };

    // A read-only store fed from the viewer's provider, so the renderer's
    // per-shape subscriptions work unchanged.
    // The editor hides zones while building; a viewer always draws them.
    const [store] = React.useState(() =>
      createEditorStore(undefined, { initialUi: { showZones: true } })
    );
    React.useEffect(() => {
      store.getState().loadDocument({
        shapes,
        gridSize,
        editingZoneWidth,
        editingZoneHeight,
        spotLabelPattern,
        lastSpotLabel
      });
    }, [
      store,
      shapes,
      gridSize,
      editingZoneWidth,
      editingZoneHeight,
      spotLabelPattern,
      lastSpotLabel
    ]);

    // Legend state, as in `ViewSVG`: category-tagged zones stay hidden
    // until their category is switched on (a cropped preview shows all),
    // hovering a legend row spotlights its zones, linked zones brighten
    // under the pointer.
    const [toggledCategories, setVisibleZoneCategories] = React.useState<
      Set<string>
    >(() => new Set());
    const visibleCategories = React.useMemo(
      () =>
        focusBounds
          ? new Set(zoneTypes.map((type) => type.id))
          : toggledCategories,
      [focusBounds, zoneTypes, toggledCategories]
    );
    const toggleZoneCategory = React.useCallback((id: string) => {
      setVisibleZoneCategories((previous) => {
        const next = new Set(previous);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return next;
      });
    }, []);
    const [categoryHover, setZoneCategoryHover] = React.useState<string | null>(
      null
    );
    const [hoveredLinkZoneId, setHoveredLinkZoneId] = React.useState<
      string | null
    >(null);
    const [, setIconCategoryHover] = React.useState<string | null>(null);
    const isAdmin = !!authenticatedUser?.permissions?.settings;
    const legend = React.useMemo(
      () =>
        computeLegendData(shapes, zoneTypes, {
          pixelsPerMeter: pixelsPerMeter ?? null,
          isAdmin
        }),
      [shapes, zoneTypes, pixelsPerMeter, isAdmin]
    );

    const renderOptions = React.useMemo<RenderOptions>(
      () => ({
        zoneTypes,
        teamColors,
        interactiveZones,
        isViewMode: true,
        highlightResource,
        zoneView: {
          visibleCategories,
          categoryHover,
          hoveredLinkZoneId,
          zoneColors,
          onZoneLinkClick,
          onLinkZoneHover: setHoveredLinkZoneId
        }
      }),
      [
        zoneTypes,
        teamColors,
        interactiveZones,
        highlightResource,
        visibleCategories,
        categoryHover,
        hoveredLinkZoneId,
        zoneColors,
        onZoneLinkClick
      ]
    );

    const legendElement =
      hideZoneLegend || focusBounds ? null : (
        <ZoneLegend
          zoneTypes={zoneTypes}
          zoneCategoryIds={legend.zoneCategoryIds}
          pointsOfInterest={legend.pointsOfInterest}
          setIconCategoryHover={setIconCategoryHover}
          visibleZoneCategories={visibleCategories}
          setVisibleZoneCategories={setVisibleZoneCategories}
          toggleZoneCategory={toggleZoneCategory}
          setZoneCategoryHover={setZoneCategoryHover}
          linkedZones={legend.linkedZones}
          onLinkedZoneClick={onZoneLinkClick}
          setHoveredLinkZoneId={setHoveredLinkZoneId}
          zoneAreasByCategory={legend.zoneAreasByCategory}
          zoneResourceCountsByCategory={legend.zoneResourceCountsByCategory}
          bottomOffset={legendBottomOffset}
        />
      );

    const focusViewBox = React.useMemo(() => {
      if (!focusBounds) return undefined;
      const marginX = Math.max(focusBounds.width * 0.1, 10);
      const marginY = Math.max(focusBounds.height * 0.1, 10);
      return `${focusBounds.x - marginX} ${focusBounds.y - marginY} ${focusBounds.width + marginX * 2} ${focusBounds.height + marginY * 2}`;
    }, [focusBounds]);

    if (focusViewBox) {
      return (
        <EditorStoreProvider store={store}>
          <RenderOptionsProvider value={renderOptions}>
            <div
              className="map-builder-svg-wrapper"
              style={{
                position: "relative",
                display: "block",
                width: "100%",
                height: "100%"
              }}
            >
              <svg
                ref={ref}
                width="100%"
                height="100%"
                viewBox={focusViewBox}
                preserveAspectRatio="xMidYMid meet"
                xmlns="http://www.w3.org/2000/svg"
                className="injected-svg map-builder-svg"
                style={style}
                data-map-svg-view
              >
                <DocumentLayer visibleArea={null} />
              </svg>
            </div>
          </RenderOptionsProvider>
        </EditorStoreProvider>
      );
    }

    if (!interactive) {
      // Same element shape as `ViewSVG`: a document-sized svg the host
      // pans and zooms itself (the map page transforms the wrapper).
      return (
        <EditorStoreProvider store={store}>
          <RenderOptionsProvider value={renderOptions}>
            <div
              className="map-builder-svg-wrapper"
              style={{ position: "relative", display: "inline-block" }}
            >
              <svg
                ref={ref}
                width={editingZoneWidth}
                height={editingZoneHeight}
                viewBox={`0 0 ${editingZoneWidth} ${editingZoneHeight}`}
                xmlns="http://www.w3.org/2000/svg"
                className="injected-svg map-builder-svg"
                style={style}
                data-map-svg-view
              >
                <g
                  transform={viewportTransform(stagePosition)}
                  style={{ willChange: "transform" }}
                >
                  <DocumentLayer visibleArea={null} />
                </g>
              </svg>
            </div>
            {legendElement}
          </RenderOptionsProvider>
        </EditorStoreProvider>
      );
    }

    return (
      <EditorStoreProvider store={store}>
        <RenderOptionsProvider value={renderOptions}>
          <div
            ref={containerRef}
            className="map-builder-svg-wrapper relative h-full w-full touch-none select-none overflow-hidden"
          >
            <svg
              ref={ref}
              width="100%"
              height="100%"
              xmlns="http://www.w3.org/2000/svg"
              className="injected-svg map-builder-svg block h-full w-full"
              style={style}
              data-map-svg-view
            >
              <g
                ref={rootRef}
                transform={viewportTransform(stagePosition)}
                style={{ willChange: "transform" }}
              >
                <rect
                  width={editingZoneWidth}
                  height={editingZoneHeight}
                  fill="white"
                />
                <DocumentLayer visibleArea={null} />
              </g>
            </svg>
            <div className="absolute top-10 right-10 flex items-center rounded-md bg-background-primary shadow-sm">
              <ZoomControls
                scale={stagePosition.scale}
                onZoomIn={() => zoomBy(1.2)}
                onZoomOut={() => zoomBy(1 / 1.2)}
                onCenter={fitToContainer}
              />
            </div>
          </div>
          {legendElement}
        </RenderOptionsProvider>
      </EditorStoreProvider>
    );
  }
);
ViewerSvg.displayName = "ViewerSvg";
